<template>
  <div class="audience-map" @mouseleave="onUserMarkerLeave">
    <Legend :items="legendItems"/>
    <mapbox ref="map" @load="onLoad">
      <moment-popup ref="popup" :data="bus.data" v-show="false"/>
      <active-user-marker ref="marker"/>
    </mapbox>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import { select } from "d3-selection";
import { get, last } from "lodash";
import { mapState, mapGetters, mapActions } from "vuex";
import { getUserById } from "store/queries/audience";
import bus from "store/busses/AudienceBus";
import MomentPopup from "../moments/MomentPopup";
import Mapbox from "views/elements/map/Mapbox";
import MapboxWrapper from "views/elements/map/MapboxWrapper";
import { makePoints } from "views/elements/map/helpers";
import Legend from "views/elements/legend/Legend";
import { ActiveUserMarker } from "components/markers";
import {
  getAudienceMapLegendItems,
  pointsLayer
} from "views/elements/map/helpers";

export default {
  name: "AudienceMap",
  components: {
    Mapbox,
    MomentPopup,
    Legend,
    ActiveUserMarker
  },
  mixins: [MapboxWrapper],
  data() {
    return {
      bus,
      marker: null,
      updateMarkers: {}
    };
  },
  computed: {
    ...mapState("audience", ["users", "completed"]),
    ...mapState("firehose", ["updates"]),
    ...mapGetters("account", ["locale"]),
    legendItems() {
      this.locale;
      return getAudienceMapLegendItems();
    }
  },
  watch: {
    users(newValue) {
      this.addUsersPoints(newValue);
    },
    "bus.userId"(userId) {
      if (!this.map.isStyleLoaded()) {
        return;
      }

      if (!userId) {
        this.removeActiveUserState();

        return;
      }

      const { longitude, latitude } = get(this.bus, "data.location");

      if (!longitude || !latitude) {
        return;
      }

      this.map
        .setPaintProperty("points", "circle-opacity", 0.08)
        .setPaintProperty("points", "circle-stroke-opacity", 0.08);
      this.marker.setLngLat([longitude, latitude]).addTo(this.map);

      select(this.marker.getElement())
        .attr("data-event-type", this.bus.data._presentEventType.toLowerCase())
        .style("display", "flex");
    }
  },
  activated() {
    this.addUsersPoints(this.users);

    if (this.completed) {
      this.scheduleUpdates();
    }
  },
  deactivated() {
    this.deactivate();
  },
  mounted() {
    this.$store.subscribe((mutation, state) => {
      if (mutation.type === "firehose/addUpdate") {
        this.addUpdateMarker(mutation.payload);
      }

      if (mutation.type === "firehose/removeUpdate") {
        this.removeUpdateMarker(mutation.payload);
      }
    });
  },
  methods: {
    ...mapActions("firehose", ["scheduleUpdates", "deactivate"]),
    onLoad() {
      this.map.addLayer(pointsLayer);
      this.map.on("mouseenter", "points", this.onUserPointHover);

      this.marker = new mapboxgl.Marker(this.$refs.marker.$el).remove();
      this.$refs.marker.$el.addEventListener("click", this.onUserMarkerClick);
      this.$refs.marker.$el.addEventListener(
        "mouseleave",
        this.onUserMarkerLeave
      );

      this.addUsersPoints(this.users);
    },
    addUsersPoints(users) {
      const points = makePoints(users);
      const source = this.map.getSource("points");

      if (!source) {
        return;
      }

      source.setData(points);
    },
    onUserPointHover(point) {
      const user = getUserById(point.features[0].properties.id);

      this.bus.setUser(user);
      this.$map.showPopup(this.$refs.popup.html, user.location);
    },
    onUserMarkerClick() {
      this.$router.push({
        name: "audience-user",
        params: { userId: this.bus.userId }
      });
      this.$map.hidePopup();
      this.removeActiveUserState();
    },
    onUserMarkerLeave() {
      this.bus.setUser(null);
      this.$map.hidePopup();
    },
    removeActiveUserState() {
      this.map
        .setPaintProperty("points", "circle-opacity", 1)
        .setPaintProperty("points", "circle-stroke-opacity", 1);

      if (this.marker) {
        this.marker.remove();
      }
    },
    addUpdateMarker(update) {
      if (!this.map || !this.$refs.marker) {
        return;
      }

      const user = getUserById(update.id);
      const { longitude, latitude } = get(user, "location");

      if (!longitude || !latitude) {
        return;
      }

      const marker = this.createUpdateMarker(update.type, [
        longitude,
        latitude
      ]);
      const popup = this.createUpdatePopup(update.text, [longitude, latitude]);

      this.updateMarkers = {
        ...this.updateMarkers,
        [update.id]: { marker, popup }
      };

      this.updateMarkers[update.id].marker.addTo(this.map);
      this.updateMarkers[update.id].popup.addTo(this.map);
    },
    removeUpdateMarker(id) {
      const { [id]: removed, ...withoutRemoved } = this.updateMarkers;

      if (!removed) {
        return;
      }

      this.updateMarkers = withoutRemoved;

      removed.marker.remove();
      removed.popup.remove();
    },
    createUpdateMarker(type, lngLat) {
      const el = this.$refs.marker.$el.cloneNode(true);

      el.classList.add("animated", "fast", "fadeIn", "pulse");
      el.setAttribute("data-event-type", type);
      el.style.display = "flex";

      return new mapboxgl.Marker(el).setLngLat(lngLat);
    },
    createUpdatePopup(text, lngLat) {
      const config = {
        anchor: "bottom",
        closeButton: false
      };
      const html = `<div class="popup animated fast fadeIn">
        <div class="popup-arrow"></div>
        <div class="popup-inner">
          <div>${text}<div>
        </div>
      </div>`;

      return new mapboxgl.Popup(config).setHTML(html).setLngLat(lngLat);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~scss/utilities/initial-variables.scss";
@import "~scss/utilities/derived-variables.scss";

.audience-map {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(58, 153, 252, 0.08),
    0 1px 2px 0 rgba($black, 0.08);
  overflow: hidden;
}

.legend {
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  z-index: 5;
  cursor: default;
}
</style>

<style lang="scss">
@import "~scss/utilities/initial-variables.scss";
@import "~scss/utilities/derived-variables.scss";

.audience-map {
  .mapbox {
    .mapboxgl-popup-content {
      &::after {
        display: none !important;
      }
    }

    .popup {
      position: relative;
      top: -20px;
      padding: 0.375rem 0.75rem;
      background-color: $white;
      border: rgba($black, 0.06);
      border-radius: 4px;
      box-shadow: 0 2px 8px 0 rgba($black, 0.1);

      .popup-arrow {
        position: absolute;
        width: 0;
        height: 0;
        bottom: 0;
        left: 50%;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 5px solid $white;
        transform: translate(-50%, 5px);
      }
    }
  }
}
</style>
